/* eslint-disable no-process-env */
import { z } from 'zod';
import { getLocation } from '@/utils/get-location';

const resolveConfig = <T>(config: { dev?: T; staging: T; production: T }): T => {
  if (process.env.ENVIRONMENT) {
    return config[process.env.ENVIRONMENT] || config.production;
  }

  if (process.env.NODE_ENV === 'development') {
    return config.dev || config.staging;
  }
  if (process.env.NODE_ENV === 'test') {
    return config.staging;
  }

  if (typeof window !== 'undefined') {
    const isStaging = getLocation().hostname.match(/(staging|dev)\.docs\.unity\.com/);

    return isStaging ? config.staging : config.production;
  }

  return config.production;
};

export const serverEnvSchema = z.object({
  CI: z.any().transform((value) => {
    if (value) {
      return true;
    }
  }),
  NODE_ENV: z
    .string()
    .optional()
    .transform((value) => {
      return value as 'production' | 'development';
    }),
  ASSET_PREFIX: z.string().optional(),
  NEXT_PUBLIC_CONTENT_REFRESH_ENABLED: z
    .string()
    .optional()
    .transform((value) => {
      return (
        value === 'true' ||
        resolveConfig({
          dev: true,
          staging: false,
          production: false,
        })
      );
    }),
  CONTENT_REFRESH_WS: z.string(),
  RELEASE: z.string().optional().prefault('local'),
  ENVIRONMENT: z
    .string()
    .optional()
    .transform((value) => {
      return value as 'staging' | 'production';
    }),
  SENTRY_DSN: z.string().optional(),
  DOCS_CONTENT_PATH: z.string(),
  DOCS_PREVIEW_PATH: z.string(),
  GENESIS_API_URL: z
    .string()
    .optional()
    .transform(() => {
      return resolveConfig({
        staging: 'https://api-staging.unity.com',
        production: 'https://api.unity.com',
      });
    }),
  GATEWAY_API_URL: z
    .string()
    .optional()
    .transform(() => {
      return resolveConfig({
        staging: 'https://staging.services.unity.com',
        production: 'https://services.unity.com',
      });
    }),
  UNITY_LOGIN_URL: z
    .string()
    .optional()
    .transform(() => {
      return resolveConfig({
        staging: 'https://staging.login.unity.com',
        production: 'https://login.unity.com',
      });
    }),
  UNITY_DASHBOARD_URL: z
    .string()
    .optional()
    .transform(() => {
      return resolveConfig({
        staging: 'https://staging.cloud.unity.com',
        production: 'https://cloud.unity.com',
      });
    }),
  UNITY_DOCS_URL: z
    .string()
    .optional()
    .transform(() => {
      return resolveConfig({
        dev: 'https://dev.docs.unity.com:3000',
        staging: 'https://staging.docs.unity.com',
        production: 'https://docs.unity.com',
      });
    }),
  ONETRUST_ID: z.string().prefault('a9f5fcb3-4c7f-4bb7-93bf-7607ffbdabe0'),
  PREVIEW: z.string().prefault('preview'),
  JIRA_API_KEY: z.string().optional(),
  JIRA_API_URL: z
    .string()
    .optional()
    .transform(() => {
      return resolveConfig({
        production: 'https://jira.unity3d.com/rest/api/2/issue/',
        staging: 'https://jiraint-stage.it.unity3d.com/rest/api/2/issue/',
      });
    }),
  NEXT_PUBLIC_COVEO_ORG_ID: z.string().prefault('unitytechnologiesnonproduction17548ciyu'),
  SHOW_UNRELEASED_CONTENT: z
    .boolean()
    .optional()
    .transform(() => {
      return resolveConfig({
        dev: true,
        staging: false,
        production: false,
      });
    }),
  PROJECT_ID: z
    .string()
    .optional()
    .transform(() => {
      return resolveConfig({
        staging: 'unity-services-foundation-stg',
        production: 'unity-services-foundation-prd',
      });
    }),
  ENABLE_INTERNAL_SEARCH: z
    .boolean()
    .optional()
    .transform(() => {
      return resolveConfig({
        dev: true,
        staging: false,
        production: false,
      });
    }),
});

const serverEnvInput: z.input<typeof serverEnvSchema> = {
  CI: process.env.CI || undefined,
  NODE_ENV: process.env.NODE_ENV || undefined,
  ASSET_PREFIX: process.env.ASSET_PREFIX || undefined,
  RELEASE: process.env.RELEASE || 'local',
  ENVIRONMENT: process.env.ENVIRONMENT || undefined,
  SENTRY_DSN: process.env.SENTRY_DSN || undefined,
  DOCS_CONTENT_PATH: process.env.DOCS_CONTENT_PATH || '/content',
  DOCS_PREVIEW_PATH: process.env.DOCS_PREVIEW_PATH || '/pr-previews',
  ONETRUST_ID: process.env.ONETRUST_ID || undefined,
  PREVIEW: process.env.PREVIEW || 'preview',
  JIRA_API_URL: process.env.JIRA_API_URL || undefined,
  JIRA_API_KEY: process.env.JIRA_API_KEY || undefined,
  NEXT_PUBLIC_CONTENT_REFRESH_ENABLED: process.env.NEXT_PUBLIC_CONTENT_REFRESH_ENABLED || 'false',
  CONTENT_REFRESH_WS: process.env.CONTENT_REFRESH_WS || 'ws://localhost:3001',
  NEXT_PUBLIC_COVEO_ORG_ID: process.env.NEXT_PUBLIC_COVEO_ORG_ID || 'unitytechnologiesnonproduction17548ciyu',
  SHOW_UNRELEASED_CONTENT: false,
  PROJECT_ID: process.env.PROJECT_ID || undefined,
  // feature-flag for internal search functionality:
  ENABLE_INTERNAL_SEARCH: false,
};

let parsedServerEnv: z.output<typeof serverEnvSchema>;

export function getServerEnv() {
  if (parsedServerEnv) {
    return parsedServerEnv;
  }
  parsedServerEnv = serverEnvSchema.parse(serverEnvInput);
  return parsedServerEnv;
}

const clientEnvSchema = serverEnvSchema.pick({
  NODE_ENV: true,
  ENVIRONMENT: true,
  RELEASE: true,
  SENTRY_DSN: true,
  ONETRUST_ID: true,
  PREVIEW: true,
});

const clientEnvInput = {
  NODE_ENV: serverEnvInput.NODE_ENV,
  ENVIRONMENT: process.env.ENVIRONMENT,
  RELEASE: serverEnvInput.RELEASE,
  SENTRY_DSN: serverEnvInput.SENTRY_DSN,
  ONETRUST_ID: serverEnvInput.ONETRUST_ID,
  PREVIEW: serverEnvInput.PREVIEW,
};

let parsedClientEnv: z.output<typeof clientEnvSchema>;

export function getClientEnv() {
  if (parsedClientEnv) {
    return parsedClientEnv;
  }
  parsedClientEnv = clientEnvSchema.parse(clientEnvInput);
  return parsedClientEnv;
}
